import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@app/layouts/AppLayout';
import { AuthLayout } from '@app/layouts/AuthLayout';
import { OnboardingLayout } from '@app/layouts/OnboardingLayout';
import { LocalizedRoutes } from '@app/routes/LocalizedRoutes';
import { ProtectedRoute, PublicRoute } from '@/routes/ProtectedRoute';
import { OnboardingGate } from '@features/billing/components/OnboardingGate';

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const NotFoundPage = lazy(() => import('@app/pages/NotFoundPage'));
const AuthSessionsPage = lazy(() => import('@features/auth/pages/AuthSessionsPage'));
const ForgotPasswordPage = lazy(() => import('@features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@features/auth/pages/ResetPasswordPage'));
const ProfilePage = lazy(() => import('@features/auth/pages/ProfilePage'));
const TeamPage = lazy(() => import('@features/team/pages/TeamPage'));
const FuelReceptionListPage = lazy(
  () => import('@features/fuelReception/pages/FuelReceptionListPage')
);
const FuelReceptionCreatePage = lazy(
  () => import('@features/fuelReception/pages/FuelReceptionCreatePage')
);
const FuelReceptionDetailPage = lazy(
  () => import('@features/fuelReception/pages/FuelReceptionDetailPage')
);
const DangerousWorkListPage = lazy(
  () => import('@features/dangerousWork/pages/DangerousWorkListPage')
);
const DangerousWorkCreatePage = lazy(
  () => import('@features/dangerousWork/pages/DangerousWorkCreatePage')
);
const DangerousWorkDetailPage = lazy(
  () => import('@features/dangerousWork/pages/DangerousWorkDetailPage')
);
const DangerousWorkReportsPage = lazy(
  () => import('@features/dangerousWork/pages/DangerousWorkReportsPage')
);
const StationChangeListPage = lazy(
  () => import('@features/changeManagement/pages/StationChangeListPage')
);
const StationChangeCreatePage = lazy(
  () => import('@features/changeManagement/pages/StationChangeCreatePage')
);
const StationChangeDetailPage = lazy(
  () => import('@features/changeManagement/pages/StationChangeDetailPage')
);
const StationChangeEditPage = lazy(
  () => import('@features/changeManagement/pages/StationChangeEditPage')
);
const StationChangeReportsPage = lazy(
  () => import('@features/changeManagement/pages/StationChangeReportsPage')
);
const IncidentListPage = lazy(() => import('@features/incidents/pages/IncidentListPage'));
const IncidentCreatePage = lazy(() => import('@features/incidents/pages/IncidentCreatePage'));
const IncidentDetailPage = lazy(() => import('@features/incidents/pages/IncidentDetailPage'));
const IncidentReportsPage = lazy(() => import('@features/reports/pages/IncidentReportsPage'));
const OfficialReportsPage = lazy(() => import('@features/reports/pages/OfficialReportsPage'));
const MonitoringReportsPage = lazy(() => import('@features/reports/pages/MonitoringReportsPage'));
const WellsConfigPage = lazy(() => import('@features/wellMonitoring/pages/WellsConfigPage'));
const WellFormPage = lazy(() => import('@features/wellMonitoring/pages/WellFormPage'));
const InspectionListPage = lazy(() => import('@features/wellMonitoring/pages/InspectionListPage'));
const InspectionCreatePage = lazy(
  () => import('@features/wellMonitoring/pages/InspectionCreatePage')
);
const InspectionDetailPage = lazy(
  () => import('@features/wellMonitoring/pages/InspectionDetailPage')
);
const MaintenanceListPage = lazy(() => import('@features/maintenance/pages/MaintenanceListPage'));
const MaintenanceCreatePage = lazy(
  () => import('@features/maintenance/pages/MaintenanceCreatePage')
);
const MaintenanceDetailPage = lazy(
  () => import('@features/maintenance/pages/MaintenanceDetailPage')
);
const MaintenanceReportsPage = lazy(
  () => import('@features/maintenance/pages/MaintenanceReportsPage')
);
const StationAssetsPage = lazy(() => import('@features/stations/pages/StationAssetsPage'));
const StationAreaFormPage = lazy(() => import('@features/stations/pages/StationAreaFormPage'));
const StationComplianceProfilePage = lazy(
  () => import('@features/stations/pages/StationComplianceProfilePage')
);
const StationTankFormPage = lazy(() => import('@features/stations/pages/StationTankFormPage'));
const BillingPage = lazy(() => import('@features/billing/pages/BillingPage'));
const BillingSuccessPage = lazy(() => import('@features/billing/pages/BillingSuccessPage'));
const BillingCancelPage = lazy(() => import('@features/billing/pages/BillingCancelPage'));
const OnboardingPage = lazy(() => import('@features/billing/pages/OnboardingPage'));
const LegalDocumentPage = lazy(() => import('@features/legal/pages/LegalDocumentPage'));
const CalendarPage = lazy(() => import('@features/compliance/pages/CalendarPage'));
const LandingPage = lazy(() => import('@features/marketing/pages/LandingPage'));
const PrivacyPage = lazy(() => import('@features/privacy/pages/PrivacyPage'));

export const router = createBrowserRouter([
  {
    // Bare root serves the marketing landing in the default language (Spanish)
    // WITHOUT redirecting to /es, so logbooks.mx stays on logbooks.mx. The
    // localized /es and /en trees below stay intact; visitors can switch via
    // the LanguageSwitcher (which lands them on /en). Reuses LocalizedRoutes,
    // whose missing :lng param falls back to DEFAULT_LANGUAGE.
    path: '/',
    element: <LocalizedRoutes />,
    children: [
      { index: true, element: <LandingPage /> },
      // The password-reset email links to `/restablecer-contrasena?uid=&token=`
      // with NO language prefix, so the route must exist at the bare root too.
      // LocalizedRoutes falls back to DEFAULT_LANGUAGE here (Spanish).
      {
        element: <AuthLayout />,
        children: [{ path: 'restablecer-contrasena', element: <ResetPasswordPage /> }]
      }
    ]
  },
  {
    path: '/:lng',
    element: <LocalizedRoutes />,
    children: [
      // Public marketing landing at the localized root (e.g. /es). Sign-in is
      // offered in a modal here; the dedicated /login page stays as a deep link.
      { index: true, element: <LandingPage /> },
      {
        element: <AuthLayout />,
        children: [
          {
            path: 'login',
            element: (
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            )
          },
          {
            path: 'register',
            element: (
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            )
          },
          {
            path: 'olvide-contrasena',
            element: (
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            )
          },
          // Reset completion is reachable with OR without a session (no
          // PublicRoute): a signed-in user who opens a reset link must not be
          // bounced to the dashboard before completing it.
          { path: 'restablecer-contrasena', element: <ResetPasswordPage /> }
        ]
      },
      // Public legal pages: reachable with or without a session (the sign-in
      // screen links here, and the privacy notice must be readable before any
      // data is collected). No auth wrapper, so they render their own layout.
      { path: 'legal/privacy', element: <LegalDocumentPage documentType="privacy_notice" /> },
      { path: 'legal/terms', element: <LegalDocumentPage documentType="terms" /> },
      // Pre-payment funnel (add station → subscribe) renders in a minimal
      // chrome with NO sidebar: an org that hasn't onboarded/paid shouldn't see
      // the full product navigation. Auth is still required; these routes are
      // the destinations the OnboardingGate steers unfinished orgs toward.
      {
        element: (
          <ProtectedRoute>
            <OnboardingLayout />
          </ProtectedRoute>
        ),
        children: [
          { path: 'onboarding', element: <OnboardingPage /> },
          // Legacy entry point: the funnel is now a single guided page; render it
          // here too so old links / bookmarks keep working.
          { path: 'onboarding/station', element: <OnboardingPage /> },
          { path: 'billing', element: <BillingPage /> },
          { path: 'billing/success', element: <BillingSuccessPage /> },
          { path: 'billing/cancel', element: <BillingCancelPage /> }
        ]
      },
      {
        element: (
          <ProtectedRoute>
            <OnboardingGate>
              <AppLayout />
            </OnboardingGate>
          </ProtectedRoute>
        ),
        children: [
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'compliance/calendar', element: <CalendarPage /> },
          { path: 'fuel-receptions', element: <FuelReceptionListPage /> },
          { path: 'fuel-receptions/new', element: <FuelReceptionCreatePage /> },
          { path: 'fuel-receptions/:id', element: <FuelReceptionDetailPage /> },
          { path: 'incidents', element: <IncidentListPage /> },
          { path: 'incidents/new', element: <IncidentCreatePage /> },
          { path: 'incidents/:id', element: <IncidentDetailPage /> },
          { path: 'reports/incidents', element: <IncidentReportsPage /> },
          { path: 'dangerous-work', element: <DangerousWorkListPage /> },
          { path: 'dangerous-work/new', element: <DangerousWorkCreatePage /> },
          { path: 'dangerous-work/:id', element: <DangerousWorkDetailPage /> },
          { path: 'reports/dangerous-work', element: <DangerousWorkReportsPage /> },
          { path: 'station-changes', element: <StationChangeListPage /> },
          { path: 'station-changes/new', element: <StationChangeCreatePage /> },
          { path: 'station-changes/:id', element: <StationChangeDetailPage /> },
          { path: 'station-changes/:id/edit', element: <StationChangeEditPage /> },
          { path: 'reports/station-changes', element: <StationChangeReportsPage /> },
          { path: 'reports/official', element: <OfficialReportsPage /> },
          { path: 'reports/monitoring', element: <MonitoringReportsPage /> },
          { path: 'monitoring/inspections', element: <InspectionListPage /> },
          { path: 'monitoring/inspections/new', element: <InspectionCreatePage /> },
          { path: 'monitoring/inspections/:id', element: <InspectionDetailPage /> },
          { path: 'maintenance/records', element: <MaintenanceListPage /> },
          { path: 'maintenance/records/new', element: <MaintenanceCreatePage /> },
          { path: 'maintenance/records/:id', element: <MaintenanceDetailPage /> },
          { path: 'maintenance/reports', element: <MaintenanceReportsPage /> },
          { path: 'monitoring/wells', element: <WellsConfigPage /> },
          { path: 'monitoring/wells/new', element: <WellFormPage /> },
          { path: 'monitoring/wells/:wellId/edit', element: <WellFormPage /> },
          { path: 'settings', element: <Navigate to="dashboard" replace /> },
          { path: 'settings/profile', element: <ProfilePage /> },
          { path: 'settings/users', element: <TeamPage /> },
          { path: 'settings/sessions', element: <AuthSessionsPage /> },
          { path: 'settings/privacy', element: <PrivacyPage /> },
          { path: 'stations/:stationId/assets', element: <Navigate to="../areas" replace /> },
          { path: 'stations/:stationId/areas', element: <StationAssetsPage mode="areas" /> },
          { path: 'stations/:stationId/areas/new', element: <StationAreaFormPage /> },
          { path: 'stations/:stationId/areas/:areaId/edit', element: <StationAreaFormPage /> },
          { path: 'stations/:stationId/tanks', element: <StationAssetsPage mode="tanks" /> },
          { path: 'stations/:stationId/tanks/new', element: <StationTankFormPage /> },
          { path: 'stations/:stationId/tanks/:tankId/edit', element: <StationTankFormPage /> },
          {
            path: 'stations/:stationId/dispensers',
            element: <StationAssetsPage mode="dispensers" />
          },
          {
            path: 'stations/:stationId/compliance-profile',
            element: <StationComplianceProfilePage />
          }
        ]
      },
      { path: '*', element: <NotFoundPage /> }
    ]
  }
]);
